import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import {combineLatest, defer, EMPTY, Observable, ReplaySubject} from 'rxjs';
import {catchError, map, take, tap} from 'rxjs/operators';
import {extractListResponse, handleResponseError, ListResponse} from '../common/api-helpers';
import {createPendingRequest, PendingRequest} from '../common/request-synchronization';
import {bindWith} from '../common/rxjs/bind-with';
import {AuthService} from '../components/auth/auth.service';
import {UserRole} from '../enums/user-role';
import {CommonListFilterQueryParamsFactory} from '../factories/common-list-query-params.factory';
import {CommonListFilter} from '../models/filters/common-list-filter';
import {User} from '../models/user';
import {AppConfigService} from './config.service';

@Injectable()
export class UserService {

    /**
     * Cleans the user object from extra data.
     * @param {User} user
     * @returns {User}
     */
    private static getClearUser(user: User): User {

        // Assume we don't have any extra data in user model.
        return user;
    }

    public readonly user$: Observable<User>;
    public user: User = null;

    protected userListUrl: string = this.appConfigService.api['userList'];

    private readonly userSubject$ = new ReplaySubject<User>(1);

    private readonly currentUserApiPathMap: {[userRole in UserRole]: string} = {
        [UserRole.ADMIN]:        this.appConfigService.api['backofficeCurrentUser'],
        [UserRole.TRADER]:       this.appConfigService.api['currentUser'],
        [UserRole.TRADER_ADMIN]: this.appConfigService.api['currentUser']
    };

    private readonly changePasswordApiPathMap: { [userRole in UserRole]: string } = {
        [UserRole.ADMIN]:        this.appConfigService.api['backofficeChangePassword'],
        [UserRole.TRADER]:       this.appConfigService.api['changePassword'],
        [UserRole.TRADER_ADMIN]: this.appConfigService.api['changePassword']
    };

    private pendingCurrentUserRequest: PendingRequest<User>;

    constructor(
        private httpClient: HttpClient,
        private appConfigService: AppConfigService,
        private authService: AuthService,
        private injector: Injector
    ) {
        this.user$ = this.userSubject$.asObservable();

        this.listenToAuthorization();
    }

    public resolveUserData(): Observable<any> {
        return combineLatest([
            this.user$.pipe(

                // [HED-930] There are no accounts in DEX
                // filter(user => !!user),
                // switchMap(() =>
                //     this.authService.userRole === UserRole.ADMIN
                //         ? this.injector.get(BackofficeAccountService).getAllAccounts()
                //         : this.injector.get(AccountService).getAllAccounts()
                // )
            )
        ]).pipe(
            take(1),
            bindWith(defer(() =>
                this.user
                    ? EMPTY
                    : this.fetchCurrentUser()
            ))
        );
    }

    public requestPasswordChange(
        currentPassword: string,
        password: string,
        twoFACode: string
    ): Observable<any> {
        const passwordChangeRequestUrl: string = this.changePasswordApiPathMap[this.authService.userRole];
        const payload = {
            oldPassword: this.authService.encodePassword(currentPassword),
            newPassword: this.authService.encodePassword(password),
            twoFACode: twoFACode && twoFACode.replace(/\s/g, '')
        };

        return this.httpClient.post(passwordChangeRequestUrl, payload);
    }

    public fetchCurrentUser(): Observable<User> {
        const request$ = this.createCurrentUserRequest();

        const pendingRequest = this.pendingCurrentUserRequest
            || createPendingRequest(
                request$,
                newPendingRequest => this.pendingCurrentUserRequest = newPendingRequest,
                () => this.pendingCurrentUserRequest = undefined
            );

        return pendingRequest.request$;
    }

    public getUsers(listFilter: CommonListFilter): Observable<ListResponse<User>> {
        let requestParams: HttpParams;

        if (listFilter) {
            const params = CommonListFilterQueryParamsFactory.createQueryParamsAsString(listFilter);
            requestParams = new HttpParams({fromString: params});
        }

        return this.httpClient.get<ListResponse<User>>(this.userListUrl, {params: requestParams}).pipe(
            map(extractListResponse),
            catchError(handleResponseError)
        );
    }

    // public autocomplete(term: string): Observable<ListResponse<T>> {
    //     return this.httpClient.get<ListResponse<User>>(this.userListUrl).pipe(
    //         map(extractListResponse),
    //         catchError(handleResponseError)
    //     );
    // }

    private listenToAuthorization(): void {
        this.authService.userRole$.pipe(
            tap((userRole: UserRole) => {
                if (!userRole) {
                    this.setUser(null);
                }
            })
        ).subscribe();
    }

    private setUser(user: User): void {
        const hasUpdate: boolean = !!user || !!this.user;

        if (user) {
            this.authService.setUserRole(user.role);
        }

        if (hasUpdate) {
            user = user
                ? UserService.getClearUser(user)
                : null;

            this.user = user;
            this.userSubject$.next(user);
        }
    }

    private createCurrentUserRequest(): Observable<User> {
        const currentUserUrl = this.currentUserApiPathMap[this.authService.userRole];

        return this.httpClient.get<User>(currentUserUrl).pipe(
            catchError(handleResponseError),
            tap(user => this.setUser(user))
        );
    }

}
